import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';

import { platformApi, Transcript, TranscriptSegment } from '../../services/platformApi';

interface SyncedTranscriptProps {
  projectId: number;
  transcript: Transcript | null;
  recordingUrl: string;
  mediaType: 'audio' | 'video';
  intervieweeName: string;
  textDirection: 'ltr' | 'rtl';
}

/**
 * Index of the segment active at `currentTime`, or -1 when none.
 * The active segment is the last one whose start is at or before the playhead,
 * which keeps a segment highlighted through gaps until the next one begins.
 */
export const activeSegmentIndex = (
  segments: Pick<TranscriptSegment, 'start_seconds' | 'end_seconds'>[],
  currentTime: number
): number => {
  let active = -1;
  for (let i = 0; i < segments.length; i += 1) {
    const start = segments[i].start_seconds;
    if (start == null || start > currentTime) {
      break;
    }
    active = i;
  }
  return active;
};

const formatTimecode = (seconds: number | null | undefined): string => {
  if (seconds == null) {
    return '';
  }
  const total = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(total / 60);
  const secs = total % 60;
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

export const SyncedTranscript: React.FC<SyncedTranscriptProps> = ({
  projectId,
  transcript,
  recordingUrl,
  mediaType,
  intervieweeName,
  textDirection,
}) => {
  const [segments, setSegments] = useState<TranscriptSegment[]>([]);
  const [currentTime, setCurrentTime] = useState(0);
  const mediaRef = useRef<HTMLMediaElement | null>(null);

  useEffect(() => {
    if (!transcript) {
      setSegments([]);
      return;
    }
    let cancelled = false;
    platformApi
      .listProjectTranscriptSegments(projectId, transcript.id)
      .then((data) => {
        if (!cancelled) {
          setSegments(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSegments([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, transcript]);

  const activeIndex = useMemo(
    () => activeSegmentIndex(segments, currentTime),
    [segments, currentTime]
  );

  const seekTo = (seconds: number | null | undefined) => {
    if (seconds == null || !mediaRef.current) {
      return;
    }
    mediaRef.current.currentTime = seconds;
    mediaRef.current.play().catch(() => undefined);
  };

  const mediaProps = {
    ref: mediaRef as React.RefObject<any>,
    src: recordingUrl,
    controls: true,
    onTimeUpdate: () => setCurrentTime(mediaRef.current?.currentTime ?? 0),
  };

  return (
    <Stack spacing={1}>
      {mediaType === 'video' ? (
        <Box component="video" sx={{ width: '100%', borderRadius: 1, bgcolor: 'black' }} {...mediaProps} />
      ) : (
        <Box component="audio" sx={{ width: '100%' }} {...mediaProps} />
      )}
      {segments.length > 0 && (
        <Box sx={{ maxHeight: 320, overflowY: 'auto' }} dir={textDirection}>
          <Stack spacing={0.5}>
            {segments.map((segment, index) => (
              <Box
                key={segment.id}
                onClick={() => seekTo(segment.start_seconds)}
                sx={{
                  cursor: 'pointer',
                  borderRadius: 1,
                  p: 1,
                  bgcolor: index === activeIndex ? 'action.selected' : 'transparent',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {formatTimecode(segment.start_seconds)} ·{' '}
                  {segment.speaker_label === 'interviewer'
                    ? 'AI interviewer'
                    : segment.speaker_label === 'interviewee'
                    ? intervieweeName
                    : segment.speaker_label || ''}
                </Typography>
                <Typography variant="body2">{segment.text}</Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
};
